import { Pencil, Plus, Search, Trash2, Sparkles, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { useState, useEffect, useMemo } from "react";
import AuthorManagerService from "../../services/dashboard/AuthorManager";
import BookMetadataService from "../../services/dashboard/BookMetadataService";
import toast from "react-hot-toast";
import config from "../../config";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";

export default function AuthorManager() {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [formData, setFormData] = useState({ authorName: "", aboutAuthor: "" });
  const [authorImage, setAuthorImage] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);

  // Load authors on mount
  useEffect(() => {
    loadAuthors();
  }, []);

  const loadAuthors = async () => {
    try {
      const res = await AuthorManagerService.getAllAuthors();
      if (res.responseCode === 200 && res.errorCode === null) {
        setAuthors(res.responseData || []);
      } else {
        toast.error(res.responseMessage || "Failed to load authors.");
      }
    } catch (error) {
      toast.error("Server error while loading authors.");
      console.error(error);
    }
  };

  const loadMatchingAuthors = async (inputValue) => {
    if (!inputValue.trim()) return [];
    try {
      const res = await AuthorManagerService.searchAuthorsByName(inputValue);
      if (res.responseCode === 200 && res.errorCode === null) {
        return res.responseData.map((author) => ({
          label: author.authorName,
          value: author.authorId,
          authorData: author, // Keep original data for edit
        }));
      }
    } catch (err) {
      console.error("Author search failed", err);
    }
    return [];
  };

  const handleAuthorSelect = (selectedOption) => {
    if (!selectedOption) return;

    if (selectedOption.authorData) {
      // Existing author selected – open modal in edit mode
      handleEdit(selectedOption.authorData);
    } else {
      // New author being added – fill in form with typed name and auto-fetch details
      setFormData({ ...formData, authorName: selectedOption.label });
      handleAutoFetchAuthorInfo(selectedOption.label);
    }
  };

  // Auto-fetch author portrait photo and biography online (Wikipedia / OpenLibrary)
  const handleAutoFetchAuthorInfo = async (nameOverride) => {
    const targetName = (nameOverride || formData.authorName || "").trim();
    if (!targetName) {
      toast.error("Please enter an author name first.");
      return;
    }

    setIsFetchingProfile(true);
    try {
      const profile = await BookMetadataService.fetchAuthorProfile(targetName);
      if (profile) {
        setFormData((prev) => ({
          ...prev,
          authorName: prev.authorName || profile.authorName,
          aboutAuthor: (profile.bio || prev.aboutAuthor || "").slice(0, 1000),
        }));

        if (profile.imageUrl) {
          const imageFile = await BookMetadataService.getAuthorImageFile(
            profile.imageUrl,
            profile.authorName || targetName
          );
          if (imageFile) {
            setAuthorImage(imageFile);
            toast.success(
              `✨ Found photo & bio for "${profile.authorName}" from ${profile.source}!`
            );
          } else {
            toast.success(`Found bio for "${profile.authorName}" from ${profile.source}!`);
          }
        } else {
          toast.success(`Found bio for "${profile.authorName}" from ${profile.source}!`);
        }
      } else {
        toast.error(`No online photo/bio found for "${targetName}". You can still upload manually.`);
      }
    } catch (err) {
      console.warn("Author lookup notice:", err);
      toast.error("Could not fetch author profile online.");
    } finally {
      setIsFetchingProfile(false);
    }
  };

  // 1-Click quick fetch and update photo for existing author in the grid
  const handleQuickEnrichAuthor = async (author) => {
    const toastId = toast.loading(`Fetching photo for "${author.authorName}"...`);
    try {
      const profile = await BookMetadataService.fetchAuthorProfile(author.authorName);
      if (profile && profile.imageUrl) {
        const imageFile = await BookMetadataService.getAuthorImageFile(
          profile.imageUrl,
          author.authorName
        );
        if (imageFile) {
          const updatedPayload = {
            authorName: author.authorName,
            aboutAuthor: author.aboutAuthor || profile.bio,
          };
          const res = await AuthorManagerService.updateAuthor(
            author.authorId,
            updatedPayload,
            imageFile
          );
          if (res.responseCode === 200 && res.errorCode === null) {
            toast.success(`Photo updated for "${author.authorName}" from ${profile.source}!`, {
              id: toastId,
            });
            loadAuthors();
            return;
          }
        }
      }
      toast.error(`No photo found online for "${author.authorName}".`, { id: toastId });
    } catch (err) {
      console.warn("Enrich author notice:", err);
      toast.error("Failed to update author photo.", { id: toastId });
    }
  };

  // Handle form submission for create or update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (editingAuthor) {
        // Update author
        res = await AuthorManagerService.updateAuthor(
          editingAuthor.authorId,
          formData,
          authorImage
        );
      } else {
        // Add author
        res = await AuthorManagerService.addAuthor(formData, authorImage);
      }

      if (res.responseCode === 200 && res.errorCode === null) {
        toast.success(
          editingAuthor
            ? "Author updated successfully!"
            : "Author added successfully!"
        );
        // Reset form
        setFormData({ authorName: "", aboutAuthor: "" });
        setAuthorImage(null);
        setEditingAuthor(null);
        setShowModal(false);
        loadAuthors();
      } else {
        toast.error(res.responseMessage || "Failed to save author.");
      }
    } catch (error) {
      toast.error("Server error while saving author.");
      console.error(error);
    }
  };

  // Handle delete author
  const handleDelete = async (id) => {
    try {
      const res = await AuthorManagerService.deleteAuthor(id);

      if (res.responseCode === 200 && res.errorCode === null) {
        toast.success("Author deleted successfully!");
        loadAuthors();
      } else {
        toast.error(res.responseMessage || "Failed to delete author.");
      }
    } catch (error) {
      toast.error("Server error while deleting author.");
      console.error(error);
    }
  };

  // Handle edit button click: populate form with author data
  const handleEdit = (author) => {
    setEditingAuthor(author);
    setFormData({
      authorName: author.authorName || "",
      aboutAuthor: author.aboutAuthor || "",
    });

    if (author.authorImage) {
      setAuthorImage(`${config.url}/authorImages/${author.authorImage}`);
    } else {
      setAuthorImage(null);
    }
    setShowModal(true);
  };

  // Filter authors by search term
  const filteredAuthors = useMemo(
    () =>
      authors.filter((author) =>
        author.authorName.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [authors, searchTerm]
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1600px] mx-auto p-6">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          ← Back
        </button>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Author Manager
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage book authors and their profiles
            </p>
          </div>

          {/* Add Author Button */}
          <button
            onClick={() => {
              setEditingAuthor(null);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Author
          </button>
        </div>

      {/* Author Form Modal */}
      <Modal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingAuthor(null);
          setFormData({ authorName: "", aboutAuthor: "" });
          setAuthorImage(null);
        }}
        title={editingAuthor ? "Edit Author" : "Add Author"}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Author Name */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Author Name
              </label>
              {formData.authorName && (
                <button
                  type="button"
                  disabled={isFetchingProfile}
                  onClick={() => handleAutoFetchAuthorInfo()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-orange-600 hover:text-orange-700 bg-orange-50 dark:bg-orange-950/40 hover:bg-orange-100 dark:hover:bg-orange-900/60 rounded-lg transition-all border border-orange-200 dark:border-orange-800 disabled:opacity-50"
                  title="Search Wikipedia and OpenLibrary for author photo & bio"
                >
                  <Sparkles size={13} className={isFetchingProfile ? "animate-spin" : ""} />
                  {isFetchingProfile ? "Searching..." : "✨ Auto-Fetch Photo & Bio"}
                </button>
              )}
            </div>
            {editingAuthor ? (
              <input
                type="text"
                value={formData.authorName}
                onChange={(e) =>
                  setFormData({ ...formData, authorName: e.target.value })
                }
                placeholder="Author name"
                className="w-full rounded-md border border-gray-300 p-2 dark:bg-gray-800 dark:text-white"
              />
            ) : (
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions
                loadOptions={loadMatchingAuthors}
                onChange={handleAuthorSelect}
                placeholder="Search or enter new author name..."
                value={
                  formData.authorName
                    ? { label: formData.authorName, value: formData.authorName }
                    : null
                }
                onCreateOption={(inputValue) => {
                  setFormData({ ...formData, authorName: inputValue });
                  handleAutoFetchAuthorInfo(inputValue);
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    padding: "2px",
                    borderRadius: "0.5rem",
                    borderColor: "#ccc",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            )}
          </div>

         {/* About Author */}
<div>
  <label
    htmlFor="aboutAuthor"
    className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
  >
    About Author
  </label>
  <textarea
    id="aboutAuthor"
    value={formData.aboutAuthor || ""}
    onChange={(e) => {
      const input = e.target.value;
      if (input.length <= 1000) {
        setFormData({ ...formData, aboutAuthor: input });
      }
    }}
    placeholder="Enter details about the author"
    className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF7236] focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
    rows="4"
  ></textarea>
  <p className="text-sm text-gray-500 dark:text-gray-300 text-right">
    {(formData.aboutAuthor || "").length}/1000 characters
  </p>
</div>


          {/* Image Upload with Preview */}
          <div>
            <label
              htmlFor="authorImage"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2"
            >
              Author Image (optional)
            </label>
            <input
              id="authorImage"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setAuthorImage(file);
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border file:rounded-lg file:text-sm file:font-semibold file:bg-[#FF7236]/10 file:text-[#FF7236] hover:file:bg-[#FF7236]/20"
            />

            {/* Preview Section */}
            {authorImage && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Preview:
                </p>
                <img
                  src={
                    typeof authorImage === "string"
                      ? authorImage
                      : URL.createObjectURL(authorImage)
                  }
                  alt="Author Preview"
                  className="w-32 h-32 object-cover rounded-md border shadow"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end pt-2">
            <button
              type="submit"
              className="bg-[#FF7236] text-white px-5 py-2 rounded-md hover:bg-[#D74A0E] transition"
            >
              {editingAuthor ? "Update Author" : "Add Author"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingAuthor(null);
                setFormData({ authorName: "", aboutAuthor: "" });
                setAuthorImage(null);
              }}
              className="bg-gray-200 text-black px-5 py-2 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

        {/* Elite Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search authors by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
            />
          </div>
        </div>

        {/* Lightweight Author Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9 gap-2">
          {filteredAuthors.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No authors found.
              </p>
            </div>
          ) : (
            filteredAuthors.map((author) => {
              // Generate initials for fallback avatar
              const initials = author.authorName
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);

              return (
                <div
                  key={author.authorId}
                  className="group bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out overflow-hidden cursor-pointer"
                >
                  {/* Card Content */}
                  <div className="p-3.5 flex flex-col items-center h-full min-h-[130px]">
                    {/* Avatar - Top Section */}
                    <div className="w-12 h-12 mb-3 flex-shrink-0">
                      {author.authorImage ? (
                        <img
                          src={`${config.url}/authorImages/${author.authorImage}`}
                          alt={author.authorName}
                          className="w-full h-full rounded-full object-cover border border-gray-200 dark:border-gray-700 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-semibold text-sm shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
                          {initials}
                        </div>
                      )}
                    </div>
                    
                    {/* Author Name - Middle Section */}
                    <div className="flex-grow mb-3 w-full">
                      <h4 
                        className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight text-center"
                        title={author.authorName}
                      >
                        {author.authorName}
                      </h4>
                    </div>

                    {/* Action Buttons - Bottom Section (Hidden by Default) */}
                    <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickEnrichAuthor(author);
                        }}
                        className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded transition-all duration-150 ease-out hover:scale-110"
                        title="Auto-fetch photo & bio online"
                        aria-label="Auto-fetch photo online"
                      >
                        <Sparkles size={13} strokeWidth={2} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(author);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-all duration-150 ease-out hover:scale-110"
                        title="Edit"
                        aria-label="Edit author"
                      >
                        <Pencil size={13} strokeWidth={2} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(author.authorId);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-all duration-150 ease-out hover:scale-110"
                        title="Delete"
                        aria-label="Delete author"
                      >
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
